import React, { useEffect, useState, useCallback } from 'react';
import { Layout, Form, Select, Input, Button, Card, message, Row, Col, Upload, Space, DatePicker } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, UploadOutlined, EyeOutlined, DeleteOutlined, FilePdfOutlined, FileOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { ensureUploadMode, getUploadMode } from '../utils/uploadMode';
import { previewOssPdf, uploadFileToOss } from '../utils/ossUpload';
import AppHeader from '../components/AppHeader';
import dayjs from 'dayjs';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

const FILE_TYPES = [
  { key: 'scheme_pdf', label: '方案图PDF', accept: '.pdf', icon: <FilePdfOutlined />, required: true, maxSize: 10 },
  { key: 'scheme_dwg', label: '方案图DWG', accept: '.dwg', icon: <FileOutlined />, maxSize: 10 },
  { key: 'sheet_dwg', label: '钣金图DWG', accept: '.dwg', icon: <FileOutlined />, maxSize: 10 },
  { key: 'machining_dwg', label: '机加工DWG', accept: '.dwg', icon: <FileOutlined />, maxSize: 10 },
  { key: 'frame_dwg', label: '架子图DWG', accept: '.dwg', icon: <FileOutlined />, maxSize: 10 },
  { key: 'probe_dwg', label: '探头图DWG', accept: '.dwg', icon: <FileOutlined />, maxSize: 10 },
  { key: 'three_d_stp', label: '3D图', accept: '.stp', icon: <FileOutlined />, maxSize: 100 }
];

const DrawingFormPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { deviceType, id } = useParams();
  const [searchParams] = useSearchParams();
  const cloneFrom = searchParams.get('cloneFrom');
  
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [fileList, setFileList] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [originalData, setOriginalData] = useState(null);
  const isEdit = !!id;
  const isClone = !!cloneFrom;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isDarkMode, getBackgroundColor, getContentBg, getTextColor, getTextColorSecondary, getBorderColor } = useTheme();

  // ========== 前端自动计算函数 ==========

  // 计算探头外形尺寸
  const calculateProbeOuterSize = (frameType, probeType, probeOpenHeight, probeOpenWidth) => {
    if (!frameType || !probeType || !probeOpenHeight || !probeOpenWidth) return null;

    const DH = parseInt(probeOpenHeight);
    const DB = parseInt(probeOpenWidth);
    if (isNaN(DH) || isNaN(DB)) return null;

    let height, length, width;
    const isLight = ['轻便型', '轻便剔除型'].includes(frameType);
    const isHeavy = frameType === '承重型';
    const isSmallRoller = frameType === '小辊筒';
    const isBiscuit = frameType === '饼干机';

    if (probeType === 'C1老款' || probeType === 'C1新款') width = 280;
    else if (probeType === 'C2老款') width = 310;
    else if (probeType === 'C2新款') width = 280;
    else if (probeType === 'D' || probeType === 'K') width = 390;
    else return null;

    if (isLight) {
      if (probeType === 'C1老款') {
        height = DH <= 180 ? 400 : 100 + DH + 120;
        length = 100 + DB + 245;
      } else if (probeType === 'C1新款') {
        height = DH <= 150 ? 370 : 100 + DH + 120;
        length = 100 + DB + 325;
      } else if (probeType === 'C2老款') {
        height = DH <= 200 ? 430 : 100 + DH + 130;
        length = 100 + DB + 270;
      } else if (probeType === 'C2新款') {
        height = DH <= 120 ? 340 : 100 + DH + 120;
        length = 100 + DB + 291.3;
      }
    } else if (isHeavy) {
      if (probeType === 'C1老款') {
        height = DH <= 160 ? 400 : 120 + DH + 120;
        length = 120 + DB + 245;
      } else if (probeType === 'C1新款') {
        height = DH <= 150 ? 390 : 120 + DH + 120;
        length = 120 + DB + 325;
      } else if (probeType === 'C2老款') {
        height = DH <= 180 ? 430 : 120 + DH + 130;
        length = 120 + DB + 270;
      } else if (probeType === 'C2新款') {
        height = DH <= 100 ? 340 : 120 + DH + 120;
        length = 120 + DB + 291.3;
      } else if (probeType === 'D') {
        height = DH <= 200 ? 440 : 120 + DH + 120;
        length = 120 + DB + 370;
      } else if (probeType === 'K') {
        height = DH <= 200 ? 440 : 120 + DH + 120;
        length = 120 + DB + 385;
      }
    } else if (isSmallRoller) {
      if (probeType === 'C1老款') {
        height = DH <= 160 ? 400 : 120 + DH + 120;
        length = 100 + DB + 245;
      } else if (probeType === 'C1新款') {
        height = DH <= 150 ? 390 : 120 + DH + 120;
        length = 100 + DB + 325;
      } else if (probeType === 'C2老款') {
        height = DH <= 180 ? 430 : 120 + DH + 130;
        length = 100 + DB + 270;
      } else if (probeType === 'C2新款') {
        height = DH <= 100 ? 340 : 120 + DH + 120;
        length = 100 + DB + 291.3;
      }
    } else if (isBiscuit) {
      if (probeType === 'C1老款') {
        height = DH <= 170 ? 400 : 110 + DH + 120;
        length = 110 + DB + 245;
      } else if (probeType === 'C1新款') {
        height = DH <= 160 ? 390 : 110 + DH + 120;
        length = 120 + DB + 325;
      } else if (probeType === 'C2老款') {
        height = DH <= 190 ? 430 : 120 + DH + 130;
        length = 120 + DB + 270;
      } else if (probeType === 'C2新款') {
        height = DH <= 100 ? 340 : 120 + DH + 120;
        length = 120 + DB + 321.3;
      }
    }

    return { height, length, width };
  };

  // 计算皮带尺寸
  const calculateBeltSize = (frameType, alarmRejectMode, frameLength, probeOpenWidth, beltMaterial) => {
    if (!frameType || !alarmRejectMode || !frameLength || !probeOpenWidth || !beltMaterial) return null;

    const L = parseInt(frameLength);
    const W = parseInt(probeOpenWidth);
    if (isNaN(L) || isNaN(W)) return null;

    let length;
    if (['轻便型', '轻便剔除型'].includes(frameType)) {
      length = L * 2 + 90;
    } else if (frameType === '承重型') {
      length = L * 2 + 80;
    } else if (frameType === '小辊筒') {
      length = L * 2 + 200;
    } else if (frameType === '饼干机') {
      const modeMap = {
        '报警停机': 550, '报警倒带': 550, '报警不停机': 550,
        '推杆剔除': null, '翻板剔除': 550, '气吹剔除': null,
        '拨杆剔除': null, '一体式下沉剔除': null, '单段下沉剔除': 460,
        '左右剔除': null, '伸缩剔除': 520, '滚筒无杆气缸剔除': null
      };
      const addValue = modeMap[alarmRejectMode];
      if (addValue === null) return null;
      length = L * 2 + addValue;
    } else {
      return null;
    }

    const width = W - 50;
    const thickness = beltMaterial === 'PU' ? '1.3' : (beltMaterial === 'PVC' ? '1.5' : null);
    if (!thickness) return null;

    return { beltSize: `${length}*${width}*${thickness}`, thickness };
  };

  // 监听表单值变化，自动计算并显示
  const watchedValues = Form.useWatch([], form);

  useEffect(() => {
    if (!watchedValues) return;

    const { frame_type, probe_type, probe_open_height, probe_open_width, alarm_reject_mode, frame_length, belt_material } = watchedValues;

    // 自动计算探头外形尺寸
    // 返回 {height: 探头高度, length: 探头长度, width: 探头宽度}
    const probeOuter = calculateProbeOuterSize(frame_type, probe_type, probe_open_height, probe_open_width);
    if (probeOuter) {
      form.setFieldsValue({
        probe_length: probeOuter.length,   // 第一个字段：探头长度
        probe_width: probeOuter.width,       // 第二个字段：探头宽度
        probe_height: probeOuter.height      // 第三个字段：探头高度
      });
    }

    // 自动计算皮带尺寸和厚度
    const beltResult = calculateBeltSize(frame_type, alarm_reject_mode, frame_length, probe_open_width, belt_material);
    if (beltResult) {
      form.setFieldsValue({
        belt_thickness: beltResult.thickness,
        belt_size: beltResult.beltSize
      });
    }
  }, [watchedValues]);

  useEffect(() => {
    if (deviceType) {
      api.get(`/device-types/${deviceType}`).then(res => {
        setConfig(res.data);
      }).catch(() => {
        message.error('设备类型配置加载失败');
        navigate('/');
      });
    }
  }, [deviceType, navigate]);

  const fetchDetail = useCallback(async (targetId) => {
    if (!config || !deviceType || !targetId) return;
    try {
      const res = await api.get(`/drawings/${deviceType}/${targetId}`);
      const data = res.data;
      
      if (isEdit) {
        setOriginalData(data);
      }
      
      const formValues = { 
        creator: data.creator, 
        production_notice: data.production_notice,
        draw_date: data.draw_date ? dayjs(data.draw_date) : null 
      };
      config.fields.forEach(field => {
        formValues[field.name] = data[field.name];
      });
      form.setFieldsValue(formValues);

      if (isEdit) {
        const existing = {};
        FILE_TYPES.forEach(ft => {
          if (data[`${ft.key}_file`]) {
            existing[ft.key] = { 
              name: data[`${ft.key}_name`] || '已上传文件', 
              url: data[`${ft.key}_file`] 
            };
          }
        });
        setExistingFiles(existing);
      }
    } catch (err) {
      message.error('获取详情失败');
    }
  }, [config, deviceType, form, isEdit]);

  useEffect(() => {
    if (isEdit && config) fetchDetail(id);
    else if (isClone && config) fetchDetail(cloneFrom);
  }, [isEdit, isClone, config, fetchDetail, id, cloneFrom]);

  const beforeUpload = (file, fileType) => {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    const expectedExt = fileType === 'scheme_pdf' ? '.pdf' : fileType === 'three_d_stp' ? '.stp' : '.dwg';
    if (ext !== expectedExt) {
      message.error(`${fileType} 只能上传 ${expectedExt} 文件`);
      return Upload.LIST_IGNORE;
    }
    
    const fileTypeConfig = FILE_TYPES.find(ft => ft.key === fileType);
    const maxSizeMB = fileTypeConfig?.maxSize || 10;
    const maxSize = maxSizeMB * 1024 * 1024;
    
    if (file.size > maxSize) {
      message.error(`${fileTypeConfig?.label || fileType} 文件大小不能超过 ${maxSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleFileChange = (fileType, { fileList: newFileList }) => {
    setFileList(prev => ({ ...prev, [fileType]: newFileList }));
  };

  const handleDeleteExistingFile = async (fileType) => {
    try {
      await api.delete(`/drawings/${deviceType}/${id}/file/${fileType}`);
      message.success('文件已删除');
      setExistingFiles(prev => {
        const updated = { ...prev };
        delete updated[fileType];
        return updated;
      });
    } catch (err) {
      message.error('删除文件失败');
    }
  };

  const handlePreview = async (fileType) => {
    const existing = existingFiles[fileType];
    const currentFile = fileList[fileType] || [];
    const fileName = existing?.name || (currentFile[0]?.name);
    if (fileName) {
      const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
      if (ext !== '.pdf') {
        message.info('DWG/STP文件不支持预览，请下载后查看');
        return;
      }
    }
    try {
      await ensureUploadMode();
      if (getUploadMode() === 'oss') {
        await previewOssPdf(`/drawings/${deviceType}/${id}/preview/${fileType}`);
      } else {
        const response = await api.get(
          `/drawings/${deviceType}/${id}/preview/${fileType}`,
          { responseType: 'blob' }
        );
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      }
    } catch (err) {
      message.error('预览失败');
    }
  };

  const isChanged = (newVal, oldVal) => {
    let normalizedNew = newVal;
    if (newVal && typeof newVal === 'object' && newVal.format) normalizedNew = newVal.format('YYYY-MM-DD');
    const n1 = normalizedNew === null || normalizedNew === undefined || normalizedNew === '' ? null : String(normalizedNew).trim();
    const n2 = oldVal === null || oldVal === undefined || oldVal === '' ? null : String(oldVal).trim();
    return n1 !== n2;
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      let drawDate = values.draw_date;
      if (drawDate && typeof drawDate === 'object' && drawDate.format) drawDate = drawDate.format('YYYY-MM-DD');
      if (!drawDate) {
        message.error('请选择制图日期');
        setLoading(false);
        return;
      }

      const machinePdfFiles = fileList['scheme_pdf'] || [];
      const hasMachinePdf = machinePdfFiles.length > 0 && machinePdfFiles[0].originFileObj;
    
      if (!isEdit && !hasMachinePdf) {
        message.error('方案图PDF必须上传');
        setLoading(false);
        return;
      }

      await ensureUploadMode();
      if (getUploadMode() === 'oss') {
        const request_data = {};
        if (isEdit && originalData) {
          if (isChanged(values.creator, originalData.creator)) request_data.creator = values.creator;
          if (isChanged(values.production_notice, originalData.production_notice)) request_data.production_notice = values.production_notice || '';
          if (isChanged(drawDate, originalData.draw_date)) request_data.draw_date = drawDate;
          config.fields.forEach(field => {
            if (values[field.name] !== undefined && values[field.name] !== null) {
              if (isChanged(values[field.name], originalData[field.name])) {
                request_data[field.name] = values[field.name];
              }
            }
          });
        } else {
          request_data.creator = values.creator;
          request_data.production_notice = values.production_notice || '';
          request_data.draw_date = drawDate;
          config.fields.forEach(field => {
            if (values[field.name] !== undefined && values[field.name] !== null) {
              request_data[field.name] = values[field.name];
            }
          });
        }

        const oss_files = {};
        for (const ft of FILE_TYPES) {
          const files = fileList[ft.key];
          if (files && files.length > 0 && files[0].originFileObj) {
            oss_files[ft.key] = await uploadFileToOss(ft.key, files[0].originFileObj);
          }
        }
        request_data.ossFiles = JSON.stringify(oss_files);

        if (isEdit) {
          await api.post(`/drawing-approvals/update/${deviceType}/${id}`, request_data);
        } else {
          await api.post(`/drawings/${deviceType}`, request_data);
        }
      } else {
        const formData = new FormData();
        if (isEdit && originalData) {
          if (isChanged(values.creator, originalData.creator)) formData.append('creator', values.creator);
          if (isChanged(values.production_notice, originalData.production_notice)) formData.append('production_notice', values.production_notice || '');
          if (isChanged(drawDate, originalData.draw_date)) formData.append('draw_date', drawDate);
          config.fields.forEach(field => {
            if (values[field.name] !== undefined && values[field.name] !== null) {
              if (isChanged(values[field.name], originalData[field.name])) {
                formData.append(field.name, values[field.name]);
              }
            }
          });
        } else {
          formData.append('creator', values.creator);
          formData.append('production_notice', values.production_notice || '');
          formData.append('draw_date', drawDate);
          config.fields.forEach(field => {
            if (values[field.name] !== undefined && values[field.name] !== null) {
              formData.append(field.name, values[field.name]);
            }
          });
        }

        FILE_TYPES.forEach(ft => {
          const files = fileList[ft.key];
          if (files && files.length > 0 && files[0].originFileObj) {
            const file = files[0].originFileObj;
            const blob = new Blob([file], { type: file.type });
            const utf8File = new File([blob], file.name, { type: file.type });
            formData.append(ft.key, utf8File);
          }
        });

        if (isEdit) {
          // 修改需要审批
          await api.post(`/drawing-approvals/update/${deviceType}/${id}`, formData);
        } else {
          // 创建无需审批，直接调用 drawings 接口
          await api.post(`/drawings/${deviceType}`, formData);
        }
      }

      message.success(isEdit ? '修改申请已提交，等待领导审核' : isClone ? '复制成功' : '创建成功');
      navigate(`/drawings/${deviceType}`);
    } catch (err) {
      console.error('提交错误:', err);
      message.error(err.response?.data?.message || err.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const rules = [];
    if (field.name !== 'other_features' && field.name !== 'belt_size') {
      rules.push({ required: true, message: `请输入${field.label}` });
    }
    if (field.type === 'select') {
      return <Select placeholder="请选择">{field.options.map(o => <Option key={o} value={o}>{o}</Option>)}</Select>;
    }
    if (field.type === 'textarea') {
      return <TextArea rows={1} placeholder={field.placeholder || ''} style={{ resize: 'vertical', minHeight: 32 }} />;
    }
    if (field.type === 'number') return <Input type="number" onWheel={(e) => e.currentTarget.blur()} placeholder={field.placeholder || ''} />;
    return <Input placeholder={field.placeholder || ''} />;
  };

  const renderFileUpload = (fileType) => {
    const existing = existingFiles[fileType.key];
    const currentFile = fileList[fileType.key] || [];
    return (
      <Card 
        size="small" 
        title={<Space>{fileType.icon}{fileType.label}</Space>} 
        style={{ 
          marginBottom: 16,
          background: getContentBg(),
          border: `1px solid ${getBorderColor()}`
        }}
        headStyle={{
          background: isDarkMode ? '#1f1f1f' : '#fafafa',
          borderBottom: `1px solid ${getBorderColor()}`,
          color: getTextColor()
        }}
      >
        {isEdit && existing && currentFile.length === 0 && (
          <div style={{ 
            marginBottom: 12, 
            padding: 8, 
            background: isDarkMode ? '#162312' : '#f6ffed', 
            borderRadius: 4,
            border: isDarkMode ? '1px solid #274916' : '1px solid #b7eb8f'
          }}>
            <Space>
              <span style={{ color: '#52c41a' }}>已上传：{existing.name}</span>
              <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(fileType.key)}>预览</Button>
              {user.role === 'admin' && (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteExistingFile(fileType.key)}>删除</Button>
              )}
            </Space>
          </div>
        )}
        <Upload
          fileList={currentFile}
          beforeUpload={(file) => beforeUpload(file, fileType.key)}
          onChange={(info) => handleFileChange(fileType.key, info)}
          accept={fileType.accept}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>{currentFile.length > 0 ? '替换文件' : '选择文件'}</Button>
        </Upload>
        <div style={{ marginTop: 8, fontSize: 12, color: getTextColorSecondary() }}>
          支持 {fileType.accept} 格式，最大 {fileType.maxSize}MB
          {currentFile.length > 0 && <span style={{ color: '#1890ff', marginLeft: 8 }}>已选择：{currentFile[0].name}</span>}
        </div>
      </Card>
    );
  };

  if (!config) return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 24, color: getTextColor() }}>加载中...</Content>
    </Layout>
  );

  const basicFields = config.fields.filter(f => 
    !['probe_open_width', 'probe_open_height', 'passable_width', 'passable_height',
     'probe_length', 'probe_width', 'probe_height', 'machine_length', 'machine_width', 'machine_height',
     'belt_thickness', 'belt_material', 'belt_size',
     'belt_height_value', 'belt_height_symbol', 'belt_height_tolerance', 'other_features'].includes(f.name)
  );
  
  const probeOpenFields = config.fields.filter(f => ['probe_open_width', 'probe_open_height'].includes(f.name));
  const passableFields = config.fields.filter(f => ['passable_width', 'passable_height'].includes(f.name));
  const probeOuterFields = config.fields.filter(f => ['probe_length', 'probe_width', 'probe_height'].includes(f.name));
  const machineOuterFields = config.fields.filter(f => ['machine_length', 'machine_width', 'machine_height'].includes(f.name));
  const beltFields = config.fields.filter(f => ['belt_material', 'belt_thickness', 'belt_size'].includes(f.name));
  const beltHeightFields = config.fields.filter(f => ['belt_height_value', 'belt_height_symbol', 'belt_height_tolerance'].includes(f.name));
  const otherFeaturesField = config.fields.find(f => f.name === 'other_features');

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 24 }}>
        <Card 
          title={<span style={{ color: getTextColor() }}>{isEdit ? `修改${config.name}记录` : isClone ? `复制${config.name}记录` : `新增${config.name}记录`}</span>}
          extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/drawings/${deviceType}`)}>返回</Button>}
          style={{
            background: getContentBg(),
            border: `1px solid ${getBorderColor()}`
          }}
          headStyle={{
            background: isDarkMode ? '#1f1f1f' : '#fafafa',
            borderBottom: `1px solid ${getBorderColor()}`,
            color: getTextColor()
          }}
        >
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item name="production_notice" label={<span style={{ color: getTextColor() }}>生产通知单</span>}>
                  <Input placeholder="请输入生产通知单" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="creator" label={<span style={{ color: getTextColor() }}>制图人</span>} rules={[{ required: true, message: '请输入制图人' }]}>
                  <Input placeholder="如：谭森" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="draw_date" label={<span style={{ color: getTextColor() }}>制图日期</span>} rules={[{ required: true, message: '请选择制图日期' }]}>
                  <DatePicker format="YYYY-MM-DD" placeholder="请选择制图日期" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6} />
            </Row>

            <Card 
              title={<span style={{ color: getTextColor() }}>基础信息</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {basicFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}${field.note || ''}`}</span>}
                      rules={field.name !== 'other_features' ? [{ required: true, message: `请输入${field.label}` }] : []}
                    >
                      {renderField(field)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card 
              title={<span style={{ color: getTextColor() }}>探头开口尺寸（自动组合）</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {probeOpenFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}`}</span>}
                      rules={[{ required: true, message: `请输入${field.label}` }]}
                    >
                      {renderField(field)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <div style={{ color: getTextColorSecondary(), fontSize: 12 }}>
                规则：宽度/10取整和高度/10组合，高度如有小数位保留小数位；高度/10后不足10前面补0。如宽度500→50，高度250→25，组合为5025；高度80→08，组合为5008；高度95→09.5，组合为5009.5；高度65→06.5，组合为5006.5
              </div>
            </Card>

            <Card 
              title={<span style={{ color: getTextColor() }}>可通过尺寸</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {passableFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}`}</span>}
                      rules={[{ required: true, message: `请输入${field.label}` }]}
                    >
                      {renderField(field)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card 
              title={<span style={{ color: getTextColor() }}>探头外形尺寸（根据机架类型、探头类型、探头开口高度、探头开口宽度自动计算）</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {probeOuterFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}`}</span>}
                      rules={[]}
                    >
                      <Input 
                        disabled 
                        placeholder="自动计算" 
                        style={{ background: isDarkMode ? '#2c2c2c' : '#f5f5f5', color: getTextColor() }}
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card 
              title={<span style={{ color: getTextColor() }}>整机外形尺寸</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {machineOuterFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}`}</span>}
                      rules={[{ required: true, message: `请输入${field.label}` }]}
                    >
                      {renderField(field)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card 
              title={<span style={{ color: getTextColor() }}>皮带信息（皮带厚度和尺寸根据材质和其他参数自动计算）</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {beltFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}`}</span>}
                      rules={field.name === 'belt_material' ? [{ required: true, message: `请选择${field.label}` }] : []}
                    >
                      {field.name === 'belt_material' ? (
                        renderField(field)
                      ) : (
                        <Input 
                          disabled 
                          placeholder={field.name === 'belt_size' ? "自动计算（长*宽*厚度）" : "根据材质自动显示"}
                          style={{ background: isDarkMode ? '#2c2c2c' : '#f5f5f5', color: getTextColor() }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            <Card 
              title={<span style={{ color: getTextColor() }}>皮带输送面离地高度</span>} 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {beltHeightFields.map((field) => (
                  <Col span={8} key={field.name}>
                    <Form.Item 
                      name={field.name} 
                      label={<span style={{ color: getTextColor() }}>{`${field.label}${field.unit ? `(${field.unit})` : ''}`}</span>}
                      rules={[{ required: true, message: `请输入${field.label}` }]}
                    >
                      {renderField(field)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>
            {otherFeaturesField && (
              <Card 
                title={<span style={{ color: getTextColor() }}>其他特点</span>} 
                size="small" 
                style={{ 
                  marginBottom: 24,
                  background: getContentBg(),
                  border: `1px solid ${getBorderColor()}`
                }}
                headStyle={{
                  background: isDarkMode ? '#1f1f1f' : '#fafafa',
                  borderBottom: `1px solid ${getBorderColor()}`,
                  color: getTextColor()
                }}
              >
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item 
                      name={otherFeaturesField.name} 
                      label={<span style={{ color: getTextColor() }}>{otherFeaturesField.label}</span>}
                    >
                      <TextArea 
                        rows={4} 
                        placeholder={otherFeaturesField.placeholder || '如：防爆机箱、带油封板等'} 
                        style={{ resize: 'vertical', minHeight: 120, width: '100%' }} 
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            )}

            <Card 
              title={<span style={{ color: getTextColor() }}>图纸文件上传</span>} 
              style={{ 
                marginTop: 24, 
                marginBottom: 24,
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{
                background: isDarkMode ? '#1f1f1f' : '#fafafa',
                borderBottom: `1px solid ${getBorderColor()}`,
                color: getTextColor()
              }}
            >
              <Row gutter={24}>
                {FILE_TYPES.map(ft => (
                  <Col span={12} key={ft.key}>
                    {renderFileUpload(ft)}
                  </Col>
                ))}
              </Row>
            </Card>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} size="large">
                  {isEdit ? '提交修改申请' : isClone ? '复制创建' : '创建'}
                </Button>
                <Button size="large" onClick={() => navigate(`/drawings/${deviceType}`)}>取消</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Content>
    </Layout>
  );
};

export default DrawingFormPage;
